import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { FlyFreelyConstants } from '../constants';
import {
    AddressDto,
    AssignLicenceCommand,
    AvailableSubscriptionTiers,
    ChargesDto,
    CustomerDetailsDto,
    FeatureSetDto,
    ModifySubscriptionCommand,
    OrganisationAuthorityGroup,
    OrganisationRelationshipDto,
    OrganisationSubscriptionDto,
    PaySubscriptionCommand,
    PurchaseSubscriptionCommand,
    PurchaseSubscriptionViaCheckoutCommand,
    ResultDto,
    ShareLicencesCommand,
    SubscriptionDetailsDto,
    SubscriptionSetupOutcomeDto,
    UnassignLicenceCommand,
    UpdateSharedLicencesCommand
} from '../model/api';
import { httpParamSerializer } from './service.helpers';

export interface CurrentSubscription {
    subscriptionType: FeatureSetDto;
    startDate: string;
}

@Injectable({
    providedIn: 'root'
})
export class SubscriptionService {
    private baseUrl: string;
    private changeSource = new Subject<void>();
    change$ = this.changeSource.asObservable();

    private refreshLicencesSource = new Subject<void>();
    refreshLicences$ = this.refreshLicencesSource.asObservable();

    constructor(constants: FlyFreelyConstants, private http: HttpClient) {
        this.baseUrl = constants.API_URL;
    }

    ngOnDestroy() {
        this.changeSource.complete();
        this.refreshLicencesSource.complete();
    }

    findById(subscriptionId: number) {
        return this.http.get<OrganisationSubscriptionDto>(
            `${this.baseUrl}/webapi/subscriptions/${subscriptionId}`
        );
    }

    findCurrentSubscriptions(organisationId: number) {
        return this.http.get<OrganisationSubscriptionDto[]>(
            `${this.baseUrl}/webapi/subscriptions`,
            { params: httpParamSerializer({ organisationId }) }
        );
    }

    findSharedSubscriptions(organisationId: number) {
        return this.http.get<OrganisationRelationshipDto[]>(
            `${this.baseUrl}/webapi/organisations/${organisationId}/relationships/childRelationships/shareLicences`
        );
    }

    findAvailableSubscriptions(organisationId: number) {
        return this.http.get<AvailableSubscriptionTiers[]>(
            `${this.baseUrl}/webapi/subscriptions/available`,
            { params: httpParamSerializer({ organisationId }) }
        );
    }

    trialUpSubscription(subscriptionId: number) {
        return this.http.post<OrganisationSubscriptionDto>(
            `${this.baseUrl}/webapi/subscriptions/${subscriptionId}/trialUp`,
            null
        );
    }

    purchaseSubscriptionViaCheckout(
        command: PurchaseSubscriptionViaCheckoutCommand
    ) {
        return this.http.post<ResultDto>(
            `${this.baseUrl}/webapi/subscriptions/checkout`,
            command
        );
    }

    purchaseSubscription(command: PurchaseSubscriptionCommand) {
        return this.http.post<SubscriptionSetupOutcomeDto>(
            `${this.baseUrl}/webapi/subscriptions`,
            command
        );
    }

    payExistingSubscription(
        subscriptionId: number,
        command: PaySubscriptionCommand
    ) {
        return this.http.post<SubscriptionSetupOutcomeDto>(
            `${this.baseUrl}/webapi/subscriptions/${subscriptionId}/pay`,
            command
        );
    }

    /**
     * This function allows an organisation to add shared licences from their subscription to a child organisation.
     * This call will also start sharing the subscription if the child organisation is not sharing the subscription already.
     * @param organisationId the parent organisation that wishes to share licences
     * @param relationshipId The ID for the relationship between the parent and the child
     * @param command The shared licence command
     * @returns The shared subscription details
     */
    shareLicences(
        organisationId: number,
        relationshipId: number,
        command: ShareLicencesCommand
    ) {
        return this.http
            .post<OrganisationRelationshipDto>(
                `${this.baseUrl}/webapi/organisations/${organisationId}/relationships/childRelationships/${relationshipId}/sharedLicences`,
                command
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    assignLicence(subscriptionId: number, command: AssignLicenceCommand) {
        return this.http
            .put<OrganisationSubscriptionDto>(
                `${this.baseUrl}/webapi/subscriptions/${subscriptionId}/licences/assign`,
                command
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    /**
     * Update the number of licences shared via this relationship.
     * @param organisationId the parent organisation that owns the subscription and licences
     * @param relationshipId The ID for the relationship between the parent and the child
     * @param command The updated number of shared licences
     * @returns The shared subscription details
     */
    updateSharedLicences(
        organisationId: number,
        relationshipId: number,
        command: UpdateSharedLicencesCommand
    ) {
        return this.http
            .put<OrganisationRelationshipDto>(
                `${this.baseUrl}/webapi/organisations/${organisationId}/relationships/childRelationships/${relationshipId}/sharedLicences`,
                command
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    /**
     * Stop all licence sharing as part of this relationship
     * @param organisationId the organisation that manages the relationship
     * @param relationshipId the relation being modified
     * @returns
     */
    unshareLicences(organisationId: number, relationshipId: number) {
        return this.http
            .delete<OrganisationRelationshipDto>(
                `${this.baseUrl}/webapi/organisations/${organisationId}/relationships/childRelationships/${relationshipId}/sharedLicences`
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    unassignLicence(subscriptionId: number, command: UnassignLicenceCommand) {
        return this.http
            .put<OrganisationSubscriptionDto>(
                `${this.baseUrl}/webapi/subscriptions/${subscriptionId}/licences/unassign`,
                command
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    findCustomerDetails(organisationId: number) {
        return this.http.get<CustomerDetailsDto>(
            `${this.baseUrl}/webapi/subscriptions/customer`,
            { params: httpParamSerializer({ organisationId }) }
        );
    }

    findSubscriptionDetails(subscriptionId: number) {
        return this.http.get<SubscriptionDetailsDto>(
            `${this.baseUrl}/webapi/subscriptions/${subscriptionId}/details`
        );
    }

    cancelSubscription(subscriptionId: number) {
        return this.http
            .put<void>(
                `${this.baseUrl}/webapi/subscriptions/${subscriptionId}/cancel`,
                null
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    uncancelSubscription(subscriptionId: number) {
        return this.http
            .put<void>(
                `${this.baseUrl}/webapi/subscriptions/${subscriptionId}/uncancel`,
                null
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    previewCharge(command: ModifySubscriptionCommand) {
        return this.http.post<ChargesDto>(
            `${this.baseUrl}/webapi/subscriptions/preview`,
            command
        );
    }

    previewSubscriptionChange(
        subscriptionId: number,
        command: ModifySubscriptionCommand
    ) {
        return this.http.post<ChargesDto>(
            `${this.baseUrl}/webapi/subscriptions/${subscriptionId}/preview`,
            command
        );
    }

    updateSubscription(
        subscriptionId: number,
        command: ModifySubscriptionCommand
    ) {
        return this.http
            .put<void>(
                `${this.baseUrl}/webapi/subscriptions/${subscriptionId}`,
                command
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    getSelfServeUrl(organisationId: number) {
        return `${this.baseUrl}/webapi/subscriptions/selfServe?organisationId=${organisationId}`;
    }

    findSelfServeUrl(organisationId: number) {
        return this.http
            .get<ResultDto>(this.getSelfServeUrl(organisationId))
            .pipe(map(resp => resp.value));
    }

    findBillingAddress(organisationId: number) {
        return this.http.get<AddressDto>(
            `${this.baseUrl}/webapi/subscriptions/address?organisationId=${organisationId}`
        );
    }

    findTaxAuthorities(
        organisationId: number,
        countryCode: string = undefined
    ) {
        if (countryCode == null) {
            return this.http.get<OrganisationAuthorityGroup[]>(
                `${this.baseUrl}/webapi/subscriptions/authorities`,
                { params: httpParamSerializer({ organisationId }) }
            );
        } else {
            return this.http.get<OrganisationAuthorityGroup[]>(
                `${this.baseUrl}/webapi/subscriptions/authorities/preview`,
                { params: httpParamSerializer({ organisationId, countryCode }) }
            );
        }
    }

    refreshLicences() {
        this.refreshLicencesSource.next();
    }
}
